<template>
  <div class="wdt-picker" role="group" aria-label="Display type">
    <div v-for="family in families" :key="family.label" class="wdt-family">
      <div class="wdt-family__caption">{{ family.label }}</div>
      <div class="wdt-family__tiles">
        <button
          v-for="t in family.types"
          :key="t.value"
          type="button"
          class="wdt-tile"
          :aria-pressed="value === t.value ? 'true' : 'false'"
          :aria-label="t.name"
          @click="select(t)">
          <!-- Static SVG thumbnails (frozen strings, no user input) -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span class="wdt-tile__thumb" v-html="t.thumb"></span>
          <span class="wdt-tile__label">{{ t.label }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { log } from '@/utils/debugLogger';

/* Miniature drawings of each output so users pick by shape, not by name.
 * Fill shapes use .mut/.acc, stroked shapes use .mut-s/.acc-s — a CSS class
 * `fill:` would override an inline fill="none" attribute, so the two kinds
 * of shape need separate classes. */
const THUMBS = Object.freeze({
  datagrid: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="acc" x="2" y="3" width="40" height="5" rx="1"/><rect class="mut" x="2" y="11" width="40" height="3.4" rx="1"/><rect class="mut" x="2" y="17" width="40" height="3.4" rx="1"/><rect class="mut" x="2" y="23" width="40" height="3.4" rx="1"/></svg>',
  table: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="mut-s" x="2" y="3" width="40" height="24" rx="1.5" stroke-width="1.8"/><path class="mut-s" d="M2 10.5h40M2 16.5h40M2 22.5h40M22 10.5v16.5" stroke-width="1.5"/><rect class="acc" x="2" y="3" width="40" height="7" rx="1.5"/></svg>',
  count: '<svg viewBox="0 0 44 30" aria-hidden="true"><text x="22" y="19" text-anchor="middle" font-family="inherit" font-size="15" font-weight="600" class="acc" stroke="none">128</text><rect class="mut" x="12" y="24" width="20" height="2.4" rx="1"/></svg>',
  gauge: '<svg viewBox="0 0 44 30" aria-hidden="true"><path class="mut-s" d="M7 25a15 15 0 0 1 30 0" stroke-width="3.2" fill="none" stroke-linecap="round"/><path class="acc-s" d="M7 25a15 15 0 0 1 7.5-13" stroke-width="3.2" fill="none" stroke-linecap="round"/><path class="acc-s" d="M22 25 29 16" stroke-width="2" stroke-linecap="round"/><circle class="acc" cx="22" cy="25" r="2.4"/></svg>',
  bar: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="acc" x="2" y="4" width="36" height="5" rx="1"/><rect class="mut" x="2" y="12.5" width="24" height="5" rx="1"/><rect class="mut" x="2" y="21" width="15" height="5" rx="1"/></svg>',
  column: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="mut" x="6" y="14" width="7" height="13" rx="1"/><rect class="acc" x="18.5" y="5" width="7" height="22" rx="1"/><rect class="mut" x="31" y="10" width="7" height="17" rx="1"/></svg>',
  stackedColumn: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="mut" x="6" y="17" width="7" height="10" rx="1"/><rect class="acc" x="6" y="10" width="7" height="6" rx="1"/><rect class="mut" x="18.5" y="13" width="7" height="14" rx="1"/><rect class="acc" x="18.5" y="4" width="7" height="8" rx="1"/><rect class="mut" x="31" y="19" width="7" height="8" rx="1"/><rect class="acc" x="31" y="12" width="7" height="6" rx="1"/></svg>',
  line: '<svg viewBox="0 0 44 30" aria-hidden="true"><path class="acc-s" d="M3 24 15 13l8 6 15-13" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  dualAxisLine: '<svg viewBox="0 0 44 30" aria-hidden="true"><path class="acc-s" d="M4 7 15 13l8 3 13 8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><path class="mut-s" d="M4 23 15 18l8 4 13-12" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  area: '<svg viewBox="0 0 44 30" aria-hidden="true"><path class="mut" d="M3 26v-6l11-8 9 6 18-11v19z"/><path class="acc-s" d="M3 20l11-8 9 6 18-11" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  pie: '<svg viewBox="0 0 44 30" aria-hidden="true"><circle class="mut" cx="22" cy="15" r="11"/><path class="acc" d="M22 15V4a11 11 0 0 1 10.4 14.5z"/></svg>',
  donut: '<svg viewBox="0 0 44 30" aria-hidden="true"><circle class="mut-s" cx="22" cy="15" r="9" stroke-width="4.6"/><path class="acc-s" d="M22 6a9 9 0 0 1 8.6 11.6" stroke-width="4.6"/></svg>',
  heatmap: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="mut" x="6" y="5" width="9" height="6" rx="1"/><rect class="acc" x="17" y="5" width="9" height="6" rx="1"/><rect class="mut" x="28" y="5" width="9" height="6" rx="1"/><rect class="acc" x="6" y="13" width="9" height="6" rx="1"/><rect class="mut" x="17" y="13" width="9" height="6" rx="1"/><rect class="acc" x="28" y="13" width="9" height="6" rx="1"/><rect class="mut" x="6" y="21" width="9" height="6" rx="1"/><rect class="acc" x="17" y="21" width="9" height="6" rx="1"/><rect class="mut" x="28" y="21" width="9" height="6" rx="1"/></svg>',
  mixedComparison: '<svg viewBox="0 0 44 30" aria-hidden="true"><rect class="mut" x="5" y="15" width="6" height="12" rx="1"/><rect class="acc" x="12" y="9" width="6" height="18" rx="1"/><rect class="mut" x="24" y="12" width="6" height="15" rx="1"/><rect class="acc" x="31" y="5" width="6" height="22" rx="1"/><path class="acc-s" d="M3 19 15 16 27 18 41 11" stroke-width="1.8" stroke-dasharray="3 2"/></svg>',
});

/* `value` strings and full names match the widget messageType contract
 * (previously the visualizationTypes list in EditSqlViewWidget). */
const FAMILIES = Object.freeze([
  {
    label: 'Table',
    types: [
      { value: 'datagrid', name: 'Data Grid', label: 'Data Grid' },
      { value: 'table', name: 'Table', label: 'Table' },
    ],
  },
  {
    label: 'Number',
    types: [
      { value: 'count', name: 'Number / KPI', label: 'Number / KPI' },
      { value: 'gauge', name: 'Gauge', label: 'Gauge' },
    ],
  },
  {
    label: 'Charts',
    types: [
      { value: 'bar', name: 'Bar Chart', label: 'Bar' },
      { value: 'column', name: 'Column Chart', label: 'Column' },
      { value: 'stackedColumn', name: 'Stacked Column Chart', label: 'Stacked' },
      { value: 'line', name: 'Line Chart', label: 'Line' },
      { value: 'area', name: 'Area Chart', label: 'Area' },
      { value: 'dualAxisLine', name: 'Dual-Axis Line Chart', label: 'Dual Axis' },
      { value: 'pie', name: 'Pie Chart', label: 'Pie' },
      { value: 'donut', name: 'Donut Chart', label: 'Donut' },
      { value: 'heatmap', name: 'Heatmap', label: 'Heatmap' },
      { value: 'mixedComparison', name: 'Comparison', label: 'Comparison' },
    ],
  },
]);

export default {
  name: 'WidgetDisplayTypePicker',

  props: {
    /** Selected messageType ('datagrid', 'count', 'bar', ...). v-model. */
    value: {
      type: String,
      default: 'datagrid',
    },
  },

  computed: {
    families() {
      return FAMILIES.map(f => ({
        label: f.label,
        types: f.types.map(t => ({ ...t, thumb: THUMBS[t.value] })),
      }));
    },
  },

  methods: {
    select(t) {
      if (t.value === this.value) return;
      log('WidgetDisplayTypePicker', `display type selected: ${t.value}`);
      this.$emit('input', t.value);
    },
  },
};
</script>

<style lang="scss" scoped>
.wdt-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 22px;
}

.wdt-family__caption {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: uppercase;
  /* Secondary slate (was #8a938f ~3.16:1) lifts this 10.5px uppercased header to ~5.7:1
     on white; AA for small text needs 4.5:1. */
  color: var(--ds-text-secondary, #55605c);
  margin-bottom: 6px;
}

.wdt-family__tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wdt-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 92px;
  height: 84px;
  padding: 0;
  border: 1px solid #e2e6e4;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #c9cfcc;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(3, 98, 76, 0.32);
  }
}

.wdt-tile__label {
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
  color: #55605c;
}

.wdt-tile__thumb {
  display: block;
  width: 44px;
  height: 30px;

  ::v-deep svg {
    display: block;
    width: 100%;
    height: 100%;

    .mut { fill: #d4dad7; }
    .acc { fill: #9fb8b0; }
    .mut-s { stroke: #c7cfcb; fill: none; }
    .acc-s { stroke: #9fb8b0; fill: none; }
  }
}

.wdt-tile[aria-pressed='true'] {
  border: 1.5px solid var(--v-primary-base, #03624c);
  background: #eef5f2;

  .wdt-tile__label {
    color: var(--v-primary-base, #03624c);
    font-weight: 600;
  }

  .wdt-tile__thumb ::v-deep svg {
    .acc { fill: var(--v-primary-base, #03624c); }
    .acc-s { stroke: var(--v-primary-base, #03624c); }
  }
}

.theme--dark {
  .wdt-family__caption { color: rgba(255, 255, 255, 0.5); }

  .wdt-tile {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.12);

    &:hover { border-color: rgba(255, 255, 255, 0.28); }
  }

  .wdt-tile__label { color: rgba(255, 255, 255, 0.7); }

  .wdt-tile__thumb ::v-deep svg {
    .mut { fill: rgba(255, 255, 255, 0.16); }
    .acc { fill: rgba(255, 255, 255, 0.38); }
    .mut-s { stroke: rgba(255, 255, 255, 0.2); fill: none; }
    .acc-s { stroke: rgba(255, 255, 255, 0.38); fill: none; }
  }

  .wdt-tile[aria-pressed='true'] {
    border-color: var(--v-secondary-base, #2cc295);
    background: rgba(44, 194, 149, 0.12);

    .wdt-tile__label { color: var(--v-secondary-base, #2cc295); }

    .wdt-tile__thumb ::v-deep svg {
      .acc { fill: var(--v-secondary-base, #2cc295); }
      .acc-s { stroke: var(--v-secondary-base, #2cc295); }
    }
  }
}
</style>
